import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import {
  FormControl,
  TextField,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { withStyles } from '@mui/styles';
import { StoreUtil, DateUtil } from 'doctivity-shared/utils';
import { listActiveUsers } from 'store/actions/usersActions';
import { LoadingView } from 'components/LoadingView';
import { isInternalUser } from 'components/usage/usageChartUtil';

const styles = (theme) => ({
  filters: {
    '& > *': { marginBottom: theme.spacing(1) },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterSpace: { width: 8 },
  userFilter: { minWidth: 260 },
  typeControl: { minWidth: 200 },
  rangeControl: { minWidth: 140 },
});

const minimumStartDate = new Date(2018, 0, 1);

// Filter bar for the Usage Analytics "Client Usage" tab. Mirrors
// ProviderCommentsOptions: an "All Users" default user picker (per client),
// an Event Type dropdown (replacing Encounter Type), and From/To dates.
class UsageOptions extends React.Component {
  componentDidMount() {
    const { users, usersClientId, app, dispatch } = this.props;
    if (StoreUtil.needsLoadLongCache(users) || usersClientId !== app.selectedClient) {
      dispatch(listActiveUsers(app.selectedClient));
    }
  }

  componentDidUpdate(prevProps) {
    const { app, dispatch } = this.props;
    if (app.selectedClient !== prevProps.app.selectedClient) {
      dispatch(listActiveUsers(app.selectedClient));
    }
  }

  render() {
    const { classes, users, filterUser, dateRange, adminOptions } = this.props;

    if (!StoreUtil.isLoaded(users)) {
      return <LoadingView />;
    }

    // Doctivity staff are excluded from the client's own numbers, so they are not offered
    // here by default — the picker then matches the Users table exactly. When "Show
    // internal activity" is on they arrive via adminOptions instead.
    //
    // The filter also prevents a duplicate entry: unlike admins, employees ARE members of
    // UsersToClients, so /Users returns them in the client list and they would otherwise
    // appear twice (once plain, once internal-tagged) whenever the toggle is on.
    const clientUsers = (StoreUtil.getData(users) || []).filter((u) => !isInternalUser(u));
    const options = ['All Users', ...clientUsers, ...(adminOptions || [])];

    return (
      <div className={classes.filters}>
        <FormControl className={classes.userFilter}>
          <Autocomplete
            options={options}
            getOptionLabel={(u) => {
              if (u === 'All Users') return u;
              const name = `${u.last_name}, ${u.first_name}`;
              if (u.is_admin) return `${name} (Doctivity admin)`;
              return u.is_internal ? `${name} (Doctivity)` : name;
            }}
            isOptionEqualToValue={(opt, val) => {
              if (opt === 'All Users' || val === 'All Users') return opt === val;
              return opt.id === val.id && !!opt.is_internal === !!val.is_internal;
            }}
            autoComplete
            disableClearable
            defaultValue='All Users'
            value={filterUser}
            onChange={(event, newUser) => this.props.onChangeUser(newUser)}
            renderInput={(params) => <TextField {...params} label='User' />}
          />
        </FormControl>

        <div className={classes.filterSpace} />
        <FormControl className={classes.rangeControl}>
          <DatePicker
            label='From'
            value={dateRange.startDate}
            minDate={minimumStartDate}
            maxDate={dateRange.endDate}
            onChange={(newDate) => this.props.onChangeStartDate(newDate)}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
        <div className={classes.filterSpace} />
        <FormControl className={classes.rangeControl}>
          <DatePicker
            label='To'
            value={dateRange.endDate}
            minDate={dateRange.startDate}
            maxDate={new Date()}
            onChange={(newDate) => this.props.onChangeEndDate(newDate)}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      </div>
    );
  }
}

UsageOptions.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  app: PropTypes.object,
  users: PropTypes.object,
  usersClientId: PropTypes.number,
  dateRange: PropTypes.object.isRequired,
  adminOptions: PropTypes.array,
  filterUser: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChangeUser: PropTypes.func.isRequired,
  onChangeStartDate: PropTypes.func.isRequired,
  onChangeEndDate: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const { app, users } = state;
  return {
    app,
    users: StoreUtil.get(users, StoreUtil.COMMON_LIST),
    usersClientId: users.listClientId,
  };
}

const styled = withStyles(styles)(UsageOptions);
const connected = connect(mapStateToProps)(styled);
export { connected as UsageOptions };
